import { HttpError } from '../../../lib/http';

export interface DiagnosticCoverage {
    state: 'complete' | 'partial' | 'delayed' | 'degraded' | 'unavailable' | 'disabled';
    observed_at?: string | null;
    reason?: string;
}
export interface DiagnosticEvent {
    event_id: string;
    environment: string;
    occurred_at: string;
    ingested_at: string;
    expires_at: string;
    operation: string;
    client_surface: string;
    stage: string;
    severity: string;
    error_code: string;
    release_sha: string;
    request_id: string | null;
    cf_ray: string | null;
    metadata: {
        client?: {
            mutation_state?: string;
            recovery_required?: boolean | null;
            operation_id?: string | null;
            failure_domain?: string;
        };
    };
}
export type IssueStatus =
    | 'open'
    | 'acknowledged'
    | 'investigating'
    | 'mitigated'
    | 'resolved'
    | 'reopened'
    | 'suppressed'
    | 'false_positive';
export interface DiagnosticIssue {
    id: string;
    environment: string;
    action: string;
    stage: string;
    error_code: string;
    client_origin: string;
    failure_domain: string;
    last_seen: string;
    severity: string;
    mutation_state: string;
    recovery_required: boolean | null;
    issue_status: IssueStatus;
    revision: number;
    server_release: string;
    client_release: string;
    observed_count: number;
    known_operation_count: number;
    unknown_operation_count?: number;
    affected_account_count: number;
}
export interface DiagnosticPage<T> {
    items: T[];
    next_cursor: string | null;
    coverage: DiagnosticCoverage;
    window?: { from: string; to: string; hours: number };
}

export function diagnosticIssueId(value: string | null): string | null {
    return value &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
        ? value.toLowerCase()
        : null;
}

export function diagnosticPath(kind: 'events' | 'issues', filters: Record<string, string>): string {
    const allowed = new Set(
        kind === 'events'
            ? [
                  'hours',
                  'environment',
                  'client_surface',
                  'operation',
                  'severity',
                  'error_code',
                  'stage',
                  'release_sha',
                  'event_id',
                  'operation_id',
                  'cursor'
              ]
            : ['hours', 'environment', 'cursor']
    );
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters))
        if (allowed.has(key) && value) params.set(key, value);
    return `/api/admin/${kind === 'events' ? 'client-diagnostics' : 'diagnostic-issues'}?${params}`;
}

export function diagnosticCoverage(
    data: DiagnosticPage<unknown> | undefined,
    error: unknown
): DiagnosticCoverage {
    if (
        error instanceof HttpError &&
        typeof error.body === 'object' &&
        error.body &&
        'coverage' in error.body
    ) {
        const coverage = (error.body as { coverage?: DiagnosticCoverage }).coverage;
        if (coverage?.state) return coverage;
    }
    return error ? { state: 'unavailable' } : data?.coverage || { state: 'unavailable' };
}

export function diagnosticEmptyText(coverage: DiagnosticCoverage): string {
    return coverage.state === 'complete'
        ? 'No matching captured events in this window.'
        : 'No captured rows are available. Coverage is incomplete; errors may be missing.';
}

export function diagnosticTraceUrl(event: DiagnosticEvent): string | null {
    return typeof event.cf_ray === 'string' && /^[a-f0-9]{16}(?:-[A-Z]{3})?$/i.test(event.cf_ray)
        ? `https://dash.cloudflare.com/?ray=${encodeURIComponent(event.cf_ray)}`
        : null;
}

const transitions: Record<IssueStatus, IssueStatus[]> = {
    open: ['acknowledged', 'investigating', 'suppressed', 'false_positive'],
    reopened: ['acknowledged', 'investigating', 'suppressed', 'false_positive'],
    acknowledged: ['investigating', 'mitigated', 'suppressed', 'false_positive'],
    investigating: ['mitigated', 'resolved', 'suppressed', 'false_positive'],
    mitigated: ['investigating', 'resolved', 'suppressed', 'false_positive'],
    resolved: ['reopened', 'suppressed', 'false_positive'],
    suppressed: ['reopened', 'investigating'],
    false_positive: ['reopened', 'investigating']
};
export function issueTransitions(status: IssueStatus): IssueStatus[] {
    return transitions[status] || [];
}

export function issueStatusReason(status: IssueStatus): string {
    const reasons: Record<IssueStatus, string> = {
        open: 'reopened_for_regression',
        acknowledged: 'triage_acknowledged',
        investigating: 'investigation_started',
        mitigated: 'mitigation_applied',
        resolved: 'resolved_verified',
        reopened: 'reopened_for_regression',
        suppressed: 'suppressed_duplicate',
        false_positive: 'false_positive_confirmed'
    };
    return reasons[status];
}
